import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/buttons';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ShoppingCart, Clock, Package, AlertTriangle } from 'lucide-react';
import Navigation from '../components/Navigation';
import PriceTicker from '../components/PriceTicker';
import Footer from '../components/Footer';
import ButcherCutCalculator from '../components/butcher/ButcherCutCalculator';
import { getApiBaseUrl } from '../lib/backend';
import { useCart } from '../CartContext';
import { useProducts } from '../ProductDataContext';

const BUTCH_PROFILE_KEY = 'shiloh_butch_profile';
const BUTCH_ORDER_DRAFT_KEY = 'shiloh_butch_order_draft_v1';

const parseEstimateRangeMidpoint = (estimate) => {
  const [lowRaw, highRaw] = estimate?.total_estimated_cost || [];
  const low = Number(lowRaw);
  const high = Number(highRaw);
  if (!Number.isFinite(low) || !Number.isFinite(high) || low <= 0 || high <= 0) {
    return null;
  }
  return Number((((low + high) / 2)).toFixed(2));
};

const ProductPage = () => {
  const { products, loading, error } = useProducts();
  const { cart, setItemQuantity, setItemPriceOverride, resolveUnitPrice } = useCart();
  const navigate = useNavigate();
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [orderError, setOrderError] = useState(null);
  const [orderForm, setOrderForm] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_address: '',
    notes: ''
  });

  const API = getApiBaseUrl();

  useEffect(() => {
    hydrateOrderFormFromButchProfile();
  }, []);

  const hydrateOrderFormFromButchProfile = () => {
    try {
      const storedProfile = JSON.parse(localStorage.getItem(BUTCH_PROFILE_KEY) || '{}');
      if (!storedProfile) {
        return;
      }

      setOrderForm((current) => ({
        ...current,
        customer_name: storedProfile.name || current.customer_name,
        customer_email: storedProfile.email || current.customer_email,
        customer_phone: storedProfile.phone || current.customer_phone
      }));
    } catch (storageError) {
      console.error('Failed to restore Butch profile for order form:', storageError);
    }
  };

  const persistVisitorProfile = (profile) => {
    try {
      const current = JSON.parse(localStorage.getItem(BUTCH_PROFILE_KEY) || '{}');
      const merged = {
        ...current,
        ...profile
      };
      localStorage.setItem(BUTCH_PROFILE_KEY, JSON.stringify(merged));
      setOrderForm((existing) => ({
        ...existing,
        customer_name: merged.name || existing.customer_name,
        customer_email: merged.email || existing.customer_email,
        customer_phone: merged.phone || existing.customer_phone,
      }));
    } catch (storageError) {
      console.error('Failed to persist visitor profile:', storageError);
    }
  };

  const updateCart = (productId, quantity) => {
    setItemQuantity(productId, quantity);
  };

  const persistButchOrderDraft = ({ estimate, meatType, orderType }) => {
    try {
      localStorage.setItem(
        BUTCH_ORDER_DRAFT_KEY,
        JSON.stringify({
          estimate,
          meatType,
          orderType,
          saved_at: new Date().toISOString(),
        }),
      );
    } catch (storageError) {
      console.error('Failed to persist Butch order draft:', storageError);
    }
  };

  const resolveRecommendedProduct = ({ meatType, orderType }) => {
    const normalizedType = (meatType || '').toLowerCase();
    const normalizedOrder = (orderType || '').toLowerCase();
    const desiredType = normalizedType === 'hog'
      ? (normalizedOrder === 'half' ? 'half_hog' : 'whole_hog')
      : (normalizedOrder === 'half' ? 'half_lamb' : 'lamb_meat');

    const isAvailable = (product) => product?.is_available !== false;
    const byExactType = products.find((product) => isAvailable(product) && String(product.type || '').toLowerCase() === desiredType);
    if (byExactType) return byExactType;

    const meatKeywords = normalizedType === 'hog'
      ? ['hog', 'pork', 'swine']
      : ['lamb', 'sheep', 'mutton'];
    const byKeyword = products.find((product) => {
      if (!isAvailable(product)) return false;
      const searchable = `${product.name || ''} ${product.description || ''} ${product.category || ''} ${product.type || ''}`.toLowerCase();
      return meatKeywords.some((keyword) => searchable.includes(keyword));
    });

    return byKeyword || null;
  };

  const handleOrderSignIn = (payload) => {
    persistButchOrderDraft(payload || {});
    navigate('/account/login');
  };

  const handleOrderCreateAccount = (payload) => {
    persistButchOrderDraft(payload || {});
    navigate('/account/register');
  };

  const handleAddRecommendedToCart = (payload) => {
    const recommended = resolveRecommendedProduct(payload || {});
    if (!recommended) {
      alert('No matching product is currently listed for this recommendation. Please sign in and we can finalize it with you.');
      return;
    }

    const estimatedUnitPrice = parseEstimateRangeMidpoint(payload?.estimate);
    if (estimatedUnitPrice) {
      setItemPriceOverride(recommended.id, estimatedUnitPrice);
    }

    setItemQuantity(recommended.id, (cart[recommended.id] || 0) + 1);
    setOrderForm((current) => ({
      ...current,
      notes: current.notes
        ? current.notes
        : `Butch recommendation: ${payload?.orderType || 'whole'} ${payload?.meatType || 'hog'}.`,
    }));
  };

  const getCartTotal = () => {
    return products.reduce((total, product) => {
      const quantity = cart[product.id] || 0;
      const price = resolveUnitPrice(product);
      return total + (quantity * price);
    }, 0);
  };

  const getCartItems = () => {
    return products.filter(product => cart[product.id] > 0).map(product => ({
      product_id: product.id,
      quantity: cart[product.id],
      price_per_unit: resolveUnitPrice(product)
    }));
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setOrderError(null);

    const orderItems = getCartItems();
    if (orderItems.length === 0) {
      alert('Please add items to your cart before ordering.');
      return;
    }

    try {
      const response = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...orderForm,
          order_items: orderItems
        }),
      });

      if (!response.ok) {
        let message = 'Failed to place order';
        try {
          const data = await response.json();
          message = data.detail || data.message || message;
        } catch (parseError) {
          console.error('Failed to parse order error payload:', parseError);
        }
        throw new Error(message);
      }

      persistVisitorProfile({
        name: orderForm.customer_name,
        email: orderForm.customer_email,
        phone: orderForm.customer_phone
      });
      alert('Order placed successfully! We will contact you soon.');
      Object.keys(cart).forEach((productId) => setItemQuantity(productId, 0));
      setShowOrderForm(false);
      setOrderForm({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_address: '',
        notes: ''
      });
    } catch (err) {
      console.error('Order submission failed:', err.message);
      setOrderError(err.message || 'Failed to place order');
    }
  };

  const hasProducts = products.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f7f3e7]">
        <Navigation />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0f5132]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error && !hasProducts) {
    return (
      <div className="min-h-screen bg-[#f7f3e7]">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <Alert className="max-w-md mx-auto">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Error loading products: {error}
            </AlertDescription>
          </Alert>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f3e7]">
      <Navigation />
      <PriceTicker />

      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Farm Products</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fresh, locally-raised products from Shiloh Ridge Farm. All products are pre-orders
            and will be available based on our production schedule.
          </p>
        </div>

        <Alert className="max-w-4xl mx-auto mb-8">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>Pre-Order Notice:</strong> All products are available for pre-order only.
            Delivery times vary based on production cycles. We will contact you when your order
            is ready for pickup or delivery.
          </AlertDescription>
        </Alert>

        {error && hasProducts && (
          <Alert className="max-w-4xl mx-auto mb-8 border-amber-300 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            <AlertDescription className="text-amber-900">
              Live product data is temporarily unavailable (`{error}`). Showing fallback products for now.
            </AlertDescription>
          </Alert>
        )}

      <div className="max-w-6xl mx-auto mb-10">
        <ButcherCutCalculator
          onOrderSignIn={handleOrderSignIn}
          onOrderCreateAccount={handleOrderCreateAccount}
          onAddRecommendedToCart={handleAddRecommendedToCart}
          onContactOrder={() => setShowOrderForm(true)}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {products.map((product) => (
          <Card key={product.id} className="h-full">
            <CardHeader>
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-xl">{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </div>
                <Badge variant="secondary" className="ml-2">
                  ${resolveUnitPrice(product).toFixed(2)}/{product.unit || 'unit'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Package className="h-4 w-4 mr-2" />
                  <span>Min Order: {product.min_order_quantity || product.minimum_order || product.min_order || 1} {product.unit || 'unit'}</span>
                </div>

                <div className="flex items-center text-sm text-gray-600">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Lead Time: {product.estimated_lead_time || (product.lead_time_days ? `${product.lead_time_days} days` : '1-2 weeks')}</span>
                </div>

                {(product.available_quantity ?? product.inventory_count) > 0 && (
                  <div className="text-sm text-green-600">
                    {(product.available_quantity ?? product.inventory_count)} {product.unit || 'unit'} available
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateCart(product.id, (cart[product.id] || 0) - 1)}
                    disabled={(cart[product.id] || 0) <= 0}
                  >
                    -
                  </Button>
                  <span className="w-12 text-center">
                    {cart[product.id] || 0}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateCart(product.id, (cart[product.id] || 0) + 1)}
                  >
                    +
                  </Button>
                  <span className="text-sm text-gray-500 ml-2">
                    {product.unit}
                  </span>
                </div>

                {cart[product.id] > 0 && (
                  <div className="text-sm font-medium">
                    Subtotal: ${((cart[product.id] || 0) * resolveUnitPrice(product)).toFixed(2)}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {getCartItems().length > 0 && (
        <div className="max-w-md mx-auto">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <ShoppingCart className="h-5 w-5 mr-2" />
                Order Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 mb-4">
                {getCartItems().map(item => {
                  const product = products.find(p => p.id === item.product_id);
                  return (
                    <div key={item.product_id} className="flex justify-between text-sm">
                      <span>{product.name} ({item.quantity} {product.unit})</span>
                      <span>${(item.quantity * item.price_per_unit).toFixed(2)}</span>
                    </div>
                  );
                })}
                <div className="border-t pt-2 font-medium">
                  <div className="flex justify-between">
                    <span>Total:</span>
                    <span>${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => setShowOrderForm(true)}
                className="w-full"
              >
                Proceed to Checkout
              </Button>
              <Link to="/cart" className="block mt-3">
                <Button variant="outline" className="w-full">
                  Open Full Cart
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      {showOrderForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle>Complete Your Order</CardTitle>
              <CardDescription>
                Please provide your contact information for delivery.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleOrderSubmit} className="space-y-4">
                {orderError && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{orderError}</AlertDescription>
                  </Alert>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">Full Name</label>
                  <input
                    type="text"
                    required
                    className="w-full p-2 border rounded-md"
                    value={orderForm.customer_name}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_name: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input
                    type="email"
                    required
                    className="w-full p-2 border rounded-md"
                    value={orderForm.customer_email}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_email: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    required
                    className="w-full p-2 border rounded-md"
                    value={orderForm.customer_phone}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_phone: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Delivery Address</label>
                  <textarea
                    required
                    rows={3}
                    className="w-full p-2 border rounded-md"
                    value={orderForm.customer_address}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, customer_address: e.target.value }))}
                    placeholder="Full address for delivery"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Special Instructions (Optional)</label>
                  <textarea
                    rows={2}
                    className="w-full p-2 border rounded-md"
                    value={orderForm.notes}
                    onChange={(e) => setOrderForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special delivery instructions or notes"
                  />
                </div>

                <div className="flex space-x-2">
                  <Button type="submit" className="flex-1">
                    Place Order
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowOrderForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
      <Footer />
    </div>
  );
};

export default ProductPage;
